/**
 * PLM Statistical QC — the v9 plots (BTLIMS "PLM Quality Control" workbench,
 * rebuilt; prototype v13 `youdenSvg` / `diffSvg`).
 *
 * Small multiples: ONE panel per analyst that has data, busiest first.
 *   • Youden plot — READ 1 (x) against READ 2 (y), the 1 : 1 line of perfect
 *     agreement, and READ 1's EPA 600 acceptable range shaded. A point outside
 *     the band is red; a ring marks a pair whose asbestos TYPE differs.
 *   • Difference vs concentration — (r1 + r2)/2 against r1 − r2, with the mean
 *     and ±2σ / ±3σ of the plotted differences.
 *   • Relative difference — the same, with (r1 − r2)/mean × 100 on y.
 *   • Reference error (standard reads) — reference against read − reference.
 *
 * Drawn as plain SVG in the prototype's viewBox (340 × 300) so the band polygon,
 * the 1 : 1 line and the ring marker are exact. All the numbers come from
 * {@link plmQcStats}; this file only draws them.
 */
import { useState, type MouseEvent } from 'react';
import { fmtLocalDate } from '../../../utils/datetime';
import { epaRange } from './plmQc';
import {
  diffSeries, niceStep, plotGroups, qcFlags, youdenOutside,
  type DiffPlot, type QcFlags, type QcPoint,
} from './plmQcStats';

export type QcPlot = 'youden' | DiffPlot;

const W = 340;
const H = 300;

type Tip = { x: number; y: number; point: QcPoint; flags: QcFlags };

interface Props {
  points: readonly QcPoint[];
  plot: QcPlot;
  /** Upper end of both axes on the Youden plot / the x axis of a difference
   *  plot: 100 (whole range) or 20 (the crowded low range). */
  zoom: 100 | 20;
  /** 'all' = one panel per analyst; a name = that analyst's single panel. */
  analyst: string;
  isRef: boolean;
}

export function PlmQcPlots({ points, plot, zoom, analyst, isRef }: Props) {
  const [tip, setTip] = useState<Tip | null>(null);
  const groups = plotGroups(points, analyst, isRef);

  const hover = (e: MouseEvent<SVGCircleElement>, point: QcPoint, flags: QcFlags) => {
    const r = (e.target as SVGCircleElement).getBoundingClientRect();
    setTip({ x: Math.min(window.innerWidth - 220, r.right + 8), y: Math.max(4, r.top - 10), point, flags });
  };

  if (!groups.length) return <div className="plm-qcplots__empty">No data.</div>;

  return (
    <>
      <div className="plm-qcplots" onMouseLeave={() => setTip(null)}>
        {groups.map(([name, g]) => (
          <div className="plm-qcplots__panel" key={name}>
            <div className="plm-qcplots__hd">{name} <span>{g.length} pts</span></div>
            {plot === 'youden'
              ? <Youden points={g} zoom={zoom} isRef={isRef} onHover={hover} />
              : <Difference points={g} zoom={zoom} isRef={isRef} rel={plot === 'rel'} onHover={hover} />}
          </div>
        ))}
      </div>
      <PlotLegend plot={plot} isRef={isRef} />
      {tip && <PlotTip tip={tip} isRef={isRef} />}
    </>
  );
}

type Hover = (e: MouseEvent<SVGCircleElement>, p: QcPoint, f: QcFlags) => void;

function Youden({ points, zoom: Z, isRef, onHover }: {
  points: QcPoint[]; zoom: number; isRef: boolean; onHover: Hover;
}) {
  const m = { l: 40, r: 12, t: 10, b: 34 };
  const X = (v: number) => m.l + Math.min(v, Z) * (W - m.l - m.r) / Z;
  const Y = (v: number) => H - m.b - Math.min(v, Z) * (H - m.t - m.b) / Z;
  const ticks = [0, 1, 2, 3, 4, 5].map(k => k * Z / 5);
  // READ 1's acceptable band, sampled every Z/50 along x.
  const band: Array<[number, number, number]> = [];
  for (let k = 0; k <= 50; k++) {
    const v = k * Z / 50;
    const r = epaRange(Math.max(v, 0.5));
    band.push([v, r.lo, r.hi]);
  }
  const bandPts = band.map(b => `${X(b[0])},${Y(b[2])}`).join(' ') + ' '
    + [...band].reverse().map(b => `${X(b[0])},${Y(b[1])}`).join(' ');
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="plm-qcplots__svg" role="img" aria-label="Youden plot">
      {ticks.map(v => (
        <g key={v}>
          <line x1={m.l} x2={W - m.r} y1={Y(v)} y2={Y(v)} className="grid" />
          <text x={m.l - 5} y={Y(v) + 4} textAnchor="end" className="ax">{v}</text>
          <text x={X(v)} y={H - m.b + 14} textAnchor="middle" className="ax">{v}</text>
        </g>
      ))}
      <polygon className="epaband" points={bandPts} />
      <line x1={X(0)} y1={Y(0)} x2={X(Z)} y2={Y(Z)} className="l-mean" />
      <text x={(W + m.l) / 2} y={H - 4} textAnchor="middle" className="axt">
        {isRef ? 'Reference %' : 'Result 1 %'}</text>
      <text x={12} y={(H - m.b) / 2} textAnchor="middle" className="axt"
        transform={`rotate(-90 12 ${(H - m.b) / 2})`}>{isRef ? 'Read %' : 'Result 2 %'}</text>
      {points.map(p => {
        const f = qcFlags(p, isRef);
        const out = youdenOutside(p);
        return (
          <circle key={p.sequenceOid} cx={X(f.r1)} cy={Y(f.r2)} r={out ? 4.5 : 3.5}
            className={`pt${out ? ' act' : ''}${f.flagType ? ' ring' : ''}`}
            onMouseEnter={e => onHover(e, p, f)} />
        );
      })}
    </svg>
  );
}

function Difference({ points, zoom: Z, isRef, rel, onHover }: {
  points: QcPoint[]; zoom: number; isRef: boolean; rel: boolean; onHover: Hover;
}) {
  const s = diffSeries(points, rel ? 'rel' : 'diff', isRef);
  if (!s) return <div className="plm-qcplots__empty">No positive pairs.</div>;
  const m = { l: 44, r: 12, t: 10, b: 34 };
  const { mean: mu, sd } = s;
  const xmax = rel ? 100 : Z;
  const ys = s.rows.map(r => r.y);
  let y0 = Math.min(...ys, mu - 3 * sd);
  let y1 = Math.max(...ys, mu + 3 * sd);
  const pad = (y1 - y0) * 0.08 || 1;
  y0 -= pad; y1 += pad;
  const X = (v: number) => m.l + Math.min(v, xmax) * (W - m.l - m.r) / xmax;
  const Y = (v: number) => m.t + (y1 - v) * (H - m.t - m.b) / (y1 - y0);
  const st = niceStep((y1 - y0) / 5);
  const yTicks: number[] = [];
  for (let v = Math.ceil(y0 / st) * st; v <= y1; v += st) yTicks.push(Number(v.toFixed(10)));
  const xTicks = [0, 1, 2, 3, 4, 5].map(k => k * xmax / 5);
  const line = (v: number, cls: string) => (
    <line x1={m.l} x2={W - m.r} y1={Y(v)} y2={Y(v)} className={cls} />
  );
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="plm-qcplots__svg" role="img"
      aria-label={isRef ? 'Reference error' : rel ? 'Relative difference' : 'Difference versus concentration'}>
      {yTicks.map(v => (
        <g key={v}>
          <line x1={m.l} x2={W - m.r} y1={Y(v)} y2={Y(v)} className="grid" />
          <text x={m.l - 5} y={Y(v) + 4} textAnchor="end" className="ax">{Number(v.toFixed(1))}</text>
        </g>
      ))}
      {xTicks.map(v => (
        <text key={v} x={X(v)} y={H - m.b + 14} textAnchor="middle" className="ax">{v}</text>
      ))}
      {line(mu + 3 * sd, 'l-act')}{line(mu + 2 * sd, 'l-warn')}{line(mu, 'l-mean')}
      {line(mu - 2 * sd, 'l-warn')}{line(mu - 3 * sd, 'l-act')}
      <text x={(W + m.l) / 2} y={H - 4} textAnchor="middle" className="axt">
        {isRef ? 'Reference %' : '(r1 + r2) / 2  %'}</text>
      <text x={12} y={(H - m.b) / 2} textAnchor="middle" className="axt"
        transform={`rotate(-90 12 ${(H - m.b) / 2})`}>
        {isRef ? 'Error %' : rel ? 'rel. diff %' : 'r1 − r2'}</text>
      {s.rows.map(r => (
        <circle key={r.point.sequenceOid} cx={X(r.x)} cy={Y(r.y)} r={r.sev === 'ok' ? 3.5 : 4.5}
          className={`pt${r.sev === 'ok' ? '' : ` ${r.sev}`}${r.flags.flagType ? ' ring' : ''}`}
          onMouseEnter={e => onHover(e, r.point, r.flags)} />
      ))}
      <text x={W - m.r} y={m.t + 10} textAnchor="end" className="lim">
        mean {mu.toFixed(2)} · σ {sd.toFixed(2)}</text>
    </svg>
  );
}

function PlotLegend({ plot, isRef }: { plot: QcPlot; isRef: boolean }) {
  if (plot === 'youden') {
    return (
      <div className="plm-charts__legend">
        <span><i className="plm-lg plm-lg--mean" />1 : 1 line (perfect agreement)</span>
        <span><i className="plm-lg plm-lg--band" />EPA 600 acceptable range for Result 1</span>
        <span><i className="plm-lg plm-lg--pt" />inside range</span>
        <span><i className="plm-lg plm-lg--pt is-act" />outside range</span>
        <span><i className="plm-lg plm-lg--pt is-ring" />asbestos type differs</span>
      </div>
    );
  }
  return (
    <div className="plm-charts__legend">
      <span><i className="plm-lg plm-lg--pt" />
        {isRef ? 'error (read − reference)' : plot === 'rel' ? 'relative difference %' : 'r1 − r2'}</span>
      <span><i className="plm-lg plm-lg--mean" />Mean</span>
      <span><i className="plm-lg plm-lg--warn" />± 2σ</span>
      <span><i className="plm-lg plm-lg--act" />± 3σ</span>
      <span><i className="plm-lg plm-lg--pt is-warn" />outside 2σ</span>
      <span><i className="plm-lg plm-lg--pt is-act" />outside 3σ</span>
    </div>
  );
}

/** The hover card (prototype `tipFor`): date, sample, both reads, the
 *  difference and the BTLIMS flags. */
function PlotTip({ tip, isRef }: { tip: Tip; isRef: boolean }) {
  const { point: p, flags: f } = tip;
  const err = isRef ? Number((f.r2 - f.r1).toFixed(2)) : f.d;
  const signed = (v: number) => `${v > 0 ? '+' : ''}${v}`;
  return (
    <div className="plm-charts__tip plm-qcplots__tip" style={{ left: tip.x, top: tip.y }} role="tooltip">
      <b>{p.date ? fmtLocalDate(p.date) : '—'}</b>
      <div>{p.sampleLayer ?? '—'}</div>
      <div>{p.analystOriginal ?? '—'}: {p.type1} {p.pct1}</div>
      <div>{p.analyst ?? '—'}: {p.type2} {p.pct2}</div>
      <div className="plm-charts__tiperr">
        {isRef ? 'Error' : 'r1 − r2'} <b>{signed(err)}</b>
        {f.rel != null && !isRef ? ` · rel ${f.rel}%` : ''} · Cat {f.category}
        {f.flagType && <> · <b>type differs</b></>}
        {f.flagAcm && <> · <b>ACM differs</b></>}
      </div>
    </div>
  );
}
